import { useRef, useCallback, useEffect } from "react";

/**
 * A generic hook to defer state updates (or any actions) based on a condition (e.g., isDragging).
 * Queues items and flushes them when the condition becomes false or manually triggered.
 * 
 * @param {Object} params
 * @param {boolean|Object|Function} params.deferCondition - Boolean, ref, or getter; if truthy, actions will be queued.
 * @param {Function} params.onFlush - Callback executed for each queued item when flushing.
 * @param {boolean} params.isMounted - Optional ref or boolean to prevent updates after unmount.
 * @returns {Object} { addAction, flush, clear }
 */
export const useDeferredActions = ({
  deferCondition,
  onFlush,
  isMounted = { current: true }
}) => {
  const queueRef = useRef([]);
  const rafIdRef = useRef(null);
  
  // Create a stable ref for the condition so we don't need it in deps
  // Handle boolean, ref, and getter inputs
  const conditionRef = useRef(deferCondition);
  useEffect(() => {
    conditionRef.current = deferCondition;
  }, [deferCondition]);

  const getConditionValue = useCallback(() => {
    const c = conditionRef.current;
    if (typeof c === "function") {
      return c();
    }
    if (c && typeof c === "object" && "current" in c) {
      return c.current;
    }
    return c;
  }, []);

  const flush = useCallback((bypassCondition = false) => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (queueRef.current.length === 0) return;
    if (!isMounted.current) {
      queueRef.current = [];
      return;
    }

    // If condition still true, keep deferring (reschedule if needed)
    if (!bypassCondition && getConditionValue()) {
      return;
    }

    const items = [...queueRef.current];
    queueRef.current = [];
    
    onFlush(items);
  }, [getConditionValue, onFlush, isMounted]);

  const scheduleFlush = useCallback((bypassCondition = false) => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => flush(bypassCondition));
  }, [flush]);

  const addAction = useCallback((item) => {
    queueRef.current.push(item);
    
    if (!getConditionValue()) {
      scheduleFlush();
    }
  }, [getConditionValue, scheduleFlush]);

  const clear = useCallback(() => {
    queueRef.current = [];
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // Automatically flush when deferCondition turns false.
  // Note: This only triggers if deferCondition is a boolean prop that changes.
  // If it's a ref or getter, callers must trigger flush() explicitly.
  useEffect(() => {
    if (!getConditionValue() && queueRef.current.length > 0) {
      scheduleFlush();
    }
  }, [deferCondition, getConditionValue, scheduleFlush]);

  useEffect(() => {
    return () => clear();
  }, [clear]);

  return { addAction, flush, clear };
};
